import { displayText } from '../../terminology/store';
import * as Yup from 'yup';

// Core fields common to all session types
const coreFields = {
  date: Yup.string().required('Date is required.'),
  location: Yup.string().required('Location is required.'),
  session_passcode: Yup.string().required(() => displayText('Session Passcode is required.')),
  details: Yup.string().optional(),
  schedule_video: Yup.boolean(),
  registrants: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.transform((value) => null).nullable(),
  }),
  
};

const sessionSpecificFields = {
  arbitration: {
    name: Yup.string().required('Name is required.'),
    introducing_party: Yup.mixed()
  .test('is-valid-party', 'Introducing Party is required.', 
    value => Array.isArray(value) ? value.length > 0 : !!value)
  .required('Introducing Party is required.'),
    zoom_tech: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
  },
  deposition: {
  witness: Yup.string().required('Witness is required.'),
  volume: Yup.string().optional(),

  introducing_party: Yup.mixed()
    .test(
      'is-valid-party',
      'Introducing Party is required.',
      (value) => Array.isArray(value) ? value.length > 0 : !!value
    )
    .required('Introducing Party is required.'),

  court_reporting_firm: Yup.string().required('Court Reporting Firm is required.'),

  zoom_tech: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  }),

  registrants: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  }),

  meeting_duration: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.required('Meeting Duration is required.'),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  }),

  meeting_time: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  }),

  meeting_timezone: Yup.string().when('schedule_video', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.transform(() => null).nullable(),
  }),
},
  witness_prep: {
    witness: Yup.string().required('Witness is required.'),
    volume: Yup.string().optional(),
    title: Yup.string().optional(),
    introducing_party: Yup.string().required('Introducing Party is required.'),
    court_reporting_firm: Yup.string().optional(),
    meeting_duration: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
    zoom_tech: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
  },
  mediation: {
    name: Yup.string().required('Name is required.'),
    introducing_party: Yup.string().required('Introducing Party is required.'),
  },
  trial: {
    name: Yup.string().required(() => displayText('Trial Name is required.')),
    introducing_party: Yup.string().required('Introducing Party is required.'),
    zoom_tech: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
  },
  custom: {
    witness: Yup.string().required(() => displayText('Session Name is required.')),           // This fixes "hearing Name *"
    volume: Yup.string().optional(),
    introducing_party: Yup.mixed()
      .test('is-valid-party', 'Introducing Party is required.', 
        value => Array.isArray(value) ? value.length > 0 : !!value)
      .required('Introducing Party is required.'),
    court_reporting_firm: Yup.string().required('Reporting Lead Name is required.'), // This fixes the court_reporting_firm error
    meeting_duration: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.required('Meeting Duration is required.'),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
    meeting_host_reporter: Yup.string().when('schedule_video', {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.transform((value) => null).nullable(),
    }),
  },
};

export const createSessionSchema = (sessionType) => {
  const specificFields = sessionSpecificFields[sessionType] || {};
  return Yup.object({
    ...coreFields,
    ...specificFields,
  });
};

// Validate a single field
export const validateField = async (field, formData, setErrors, sessionType) => {
  try {
    const schema = createSessionSchema(sessionType);
    let validateData = { ...formData };
    if (!formData.schedule_video && ['zoom_tech', 'meeting_host_reporter', 'registrants', 'meeting_duration', 'meeting_time', 'meeting_timezone'].includes(field)) {
      validateData = { ...validateData, zoom_tech: null, meeting_host_reporter: null, registrants: null, meeting_duration: null, meeting_time: null, meeting_timezone: null };
    }
    await schema.validateAt(field, validateData);
    setErrors((prev) => ({ ...prev, [field]: '' }));
    return true;
  } catch (error) {
    setErrors((prev) => ({ ...prev, [field]: error.message }));
    return false;
  }
};

// Validate the entire form
export const validateForm = async (formData, setErrors, sessionType) => {
  try {
    const schema = createSessionSchema(sessionType);
    let validateData = { ...formData };
    if (!formData.schedule_video) {
      validateData = {
        ...validateData,
        zoom_tech: null,
        meeting_host_reporter: null,
        registrants: null,
        meeting_duration: null,
        meeting_time: null,
        meeting_timezone: null,
      };
    }
    await schema.validate(validateData, { abortEarly: false });
    setErrors({});
    return true;
  } catch (validationErrors) {
    const newErrors = {};
    if (validationErrors && validationErrors.inner && Array.isArray(validationErrors.inner)) {
      validationErrors.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path] = err.message;
        }
      });
    } else if (validationErrors && validationErrors.path) {
      newErrors[validationErrors.path] = validationErrors.message || 'Validation failed.';
    } else {
      newErrors.general = validationErrors.message || 'Validation failed.';
    }
    setErrors(newErrors);
    return false;
  }
};